import * as React from "react";

import { PageTitle } from "@/components/PageTitle";
import { BookingCalendar, CalendarSlot } from "@/features/booking/BookingCalendar";
import { ConversationPanel } from "@/features/home/components/ConversationPanel";
import { ChatEntry } from "@/features/home/components/types";

import { GuestIdentityRail } from "./GuestIdentityRail";
import { GuestSharingCard, SHARING_DECLINED, SHARING_REQUIRED } from "./GuestSharingCard";
import { GuestSlotConfirmCard } from "./GuestSlotConfirmCard";
import {
  AvailabilityResult,
  Booked,
  ConfirmResult,
  GuestSession,
  NOTHING_ELSE_ON_OFFER,
  NOTHING_ON_OFFER,
  STILL_STANDS,
  endGuestSession,
  getGuestSession,
  marketplaceRpc,
  mintGuestToken,
  refusalLine,
  shareGuestProfile,
} from "./guestApi";
import {
  AskDraft,
  EMPTY_DRAFT,
  QuestionId,
  applyAnswer,
  missing,
  promptFor,
  toNotes,
} from "./clarify";
import { PublicLoadingLoop } from "@/features/booking/public/PublicLoading";
import { defaultRange, slotsFromSpans } from "./spans";
import { NO_PREFERENCE, Preference, prefers, readBack, windowFor } from "./when";

/**
 * The same page, now that the visitor has proved who they are.
 *
 * <p>Not a second chat interface: the thread and composer are the components the members' page
 * mounts, given different state (D-25). What changes is that the composer works, the rail lists
 * what has been proved instead of offering to prove it, and the thread greets them by name (D-43).</p>
 *
 * <p><b>Every refusal reads the same.</b> A host who does not exist, one on a tier that cannot be
 * reached, one who has not switched reachability on, one whose rules turned this caller away and
 * one who is genuinely busy all produce one identical line, because they produce one identical
 * error on the wire (D-8). The page must not improve on that. Anything that made one refusal read
 * differently from another would hand back, in the interface, the enumeration the protocol spent
 * a timing floor and a fixed message refusing.</p>
 */
export function GuestChatPage({
  onSignedOut,
}: {
  /** Called when the session turns out to be gone, so the route can fall back to the inert view. */
  onSignedOut: () => void;
}): React.ReactElement {
  const [session, setSession] = React.useState<GuestSession | null>(null);
  const [messages, setMessages] = React.useState<ChatEntry[]>([]);
  const [draft, setDraft] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [offer, setOffer] = React.useState<Offer | null>(null);
  // The slot they pointed at, waiting to be asked for. Pointing is not asking (D-95): nothing
  // leaves this page until the card below the calendar is answered.
  const [pending, setPending] = React.useState<CalendarSlot | null>(null);
  // What is being assembled, and which of the fixed questions is outstanding. Both reset once a
  // request has gone: the loop runs before the first call and never reopens after one (D-80).
  const [wanted, setWanted] = React.useState<AskDraft>(EMPTY_DRAFT);
  const [asking, setAsking] = React.useState<QuestionId | null>(null);
  // The meeting this page most recently booked, so it can be moved without starting again. Only
  // one: the page holds a conversation, not a diary.
  const [booking, setBooking] = React.useState<Booked | null>(null);
  // Whether the card has been answered this visit. Held here rather than stored, because declining
  // is supposed to leave nothing behind — including a record that they declined.
  const [decided, setDecided] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    getGuestSession()
      .then((loaded) => {
        if (cancelled) return;
        if (!loaded.signed_in) {
          onSignedOut();
          return;
        }
        setSession(loaded);
      })
      .catch(() => {
        if (!cancelled) onSignedOut();
      });
    return () => {
      cancelled = true;
    };
  }, [onSignedOut]);

  /**
   * What they asked for at the top, and everything else still underneath.
   *
   * <p>A stable partition rather than a sort: inside each group the times keep the order they
   * arrived in, which is chronological. Nothing is dropped — the whole point is that a preference
   * decides what to look at first and never what to throw away (D-97).</p>
   */
  function preferredFirst(slots: CalendarSlot[], preference: Preference): CalendarSlot[] {
    if (!preference.from) return slots;
    const wanted = slots.filter((slot) => prefers(preference, slot.start));
    if (wanted.length === 0) return slots;
    return [...wanted, ...slots.filter((slot) => !prefers(preference, slot.start))];
  }

  function say(from: "you" | "agent", text: React.ReactNode) {
    setMessages((prior) => [...prior, { from, text }]);
  }

  async function ask(e: React.FormEvent) {
    e.preventDefault();
    if (!session || busy) return;
    const text = draft.trim();
    if (!text) return;

    // Nothing goes out for somebody nobody can reply to. Checked here rather than at the point a
    // host would see it, so the check itself carries no news about any host (D-44, D-80, D-92).
    if (!session.sharing?.consented) {
      setDraft("");
      say("you", text);
      say("agent", SHARING_REQUIRED);
      return;
    }

    setDraft("");
    say("you", text);
    setOffer(null);
    setPending(null);

    const filled = applyAnswer(wanted, asking, text);
    const outstanding = missing(filled);
    setWanted(filled);
    setAsking(outstanding);

    if (outstanding) {
      // Answered here, with no call of any kind. Nothing has been asked of any host yet, which is
      // what makes these questions incapable of describing one (D-80).
      say("agent", promptFor(outstanding));
      return;
    }

    // The request is whole. From here the loop is closed: whatever comes back, the page does not
    // ask another question, because a question after an answer is a question about that answer.
    setWanted(EMPTY_DRAFT);
    const handle = filled.handle as string;
    const preference = filled.when ?? NO_PREFERENCE;

    // Said before the call and whatever it returns, so a misread is caught here rather than after
    // a list of times that were never what they meant. It is about their own words and nothing
    // else, so it can carry nothing about any host (D-97).
    const heard = readBack(preference);
    if (heard) say("agent", heard);

    setBusy(true);
    try {
      const token = await mintGuestToken(session.audience_base, handle);
      const result = await marketplaceRpc<AvailabilityResult>(token, "a2a.queryAvailability", {
        host_ref: `@${handle}`,
        intent_manifest: { notes: toNotes(filled) },
        meeting_spec: {
          duration_minutes: filled.minutes ?? 30,
          // Widened to reach what they preferred, never narrowed to it. A preference that filtered
          // would hand back nothing on a full Wednesday and read as a host with no time at all.
          date_range: windowFor(preference),
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });

      const slots = slotsFromSpans(result.spans, result.duration_minutes);
      if (slots.length === 0) {
        say("agent", NOTHING_ON_OFFER);
        return;
      }
      setOffer({
        token,
        sessionId: result.session_id,
        handle,
        slots: preferredFirst(slots, preference),
        minutes: result.duration_minutes,
      });
      // Only when they asked for something and none of it came back. Derived entirely from the
      // times they are about to be shown, so it tells them nothing they cannot already see.
      const missedIt = preference.label && !slots.some((slot) => prefers(preference, slot.start));
      say(
        "agent",
        missedIt
          ? `Nothing on ${preference.label}. Here is what there is.`
          : `Here is when @${handle} is free. Pick one and I will ask for it.`,
      );
    } catch (refused) {
      say("agent", refusalLine(refused, onSignedOut));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Ask for a different time for a meeting that is already booked.
   *
   * <p>The one they have is not given up to ask (D-82). If nothing better is admitted, or the
   * diary cannot be read, or they simply do not pick anything, the meeting they already had is
   * still theirs — so this says so, rather than implying they are about to lose it.</p>
   */
  async function move() {
    if (!booking || busy) return;
    say("you", "Can we move it?");
    setPending(null);
    setBusy(true);
    try {
      const result = await marketplaceRpc<AvailabilityResult>(booking.token, "a2a.rescheduleBooking", {
        session_id: booking.sessionId,
        adjustments: {
          date_range: defaultRange(),
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });

      const slots = slotsFromSpans(result.spans, result.duration_minutes);
      if (slots.length === 0) {
        say("agent", NOTHING_ELSE_ON_OFFER);
        return;
      }
      setOffer({
        token: booking.token,
        sessionId: booking.sessionId,
        handle: booking.handle,
        slots,
        minutes: result.duration_minutes,
      });
      say("agent", "Here is when else they are free. Your current time stands until you pick one.");
    } catch (refused) {
      // The same single line as any other refusal, plus the one thing that is theirs to know: the
      // meeting they already have did not go anywhere.
      say("agent", `${refusalLine(refused, onSignedOut)} ${STILL_STANDS}`);
    } finally {
      setBusy(false);
    }
  }

  async function take(slot: CalendarSlot) {
    if (!offer || busy) return;
    setBusy(true);
    try {
      const result = await marketplaceRpc<ConfirmResult>(offer.token, "a2a.confirmSlot", {
        session_id: offer.sessionId,
        slot: { start: slot.start, end: slot.end },
      });
      setOffer(null);
      if (result.status === "CONFIRMED") {
        setBooking({
          sessionId: offer.sessionId,
          token: offer.token,
          handle: offer.handle,
          start: result.start,
        });
      }
      say("agent", result.status === "CONFIRMED" ? bookedLine(result) : held(result));
    } catch (refused) {
      // The same line as any other refusal. The hour going while somebody chose is one of the
      // causes the single error covers, and saying so would say something about the host's diary.
      say("agent", refusalLine(refused, onSignedOut));
    } finally {
      setPending(null);
      setBusy(false);
    }
  }

  if (!session) {
    return <PublicLoadingLoop />;
  }

  return (
    <div className="mx-auto max-w-[1140px] space-y-4 px-4 py-6 font-comic-body">
      <header>
        <PageTitle>
          {session.given_name ? `Hello, ${session.given_name}` : "Schedule through your agent"}
        </PageTitle>
        <p className="text-xs text-muted-foreground">
          Name someone with an @handle and say what you want. Your agent handles the back and forth.
        </p>
      </header>

      <div className="grid items-start gap-4 lg:grid-cols-[240px_minmax(0,1fr)]">
        <GuestIdentityRail
          identities={session.identities}
          onSignOut={() => {
            void endGuestSession().then(onSignedOut);
          }}
        />

        <div className="min-w-0">
          {/*
            Once, on arrival, before they can send anything. Not at the moment a request reaches a
            host: a question asked only of people who got through answers itself (D-44, D-80).
          */}
          {session.sharing && !session.sharing.consented && !decided && (
            <div className="mb-4">
              <GuestSharingCard
                sharing={session.sharing}
                busy={busy}
                onShare={() => {
                  setBusy(true);
                  void shareGuestProfile()
                    .then((sharing) => setSession((prior) => (prior ? { ...prior, sharing } : prior)))
                    .catch(() => undefined)
                    .finally(() => {
                      setDecided(true);
                      setBusy(false);
                    });
                }}
                onDecline={() => {
                  setDecided(true);
                  say("agent", SHARING_DECLINED);
                }}
              />
            </div>
          )}

          <ConversationPanel
            mode="outgoing"
            messages={messages}
            draft={draft}
            onDraftChange={setDraft}
            onSubmit={(e) => void ask(e)}
            busy={busy}
            emptyHint={
              <span>
                Try “45 minutes with @ada about a Series A follow up”, or just say who.
              </span>
            }
            belowComposer={
              booking && !offer ? (
                <div className="mt-3">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void move()}
                    className="rounded border border-border px-3 py-1 text-[11px] font-comic-headline uppercase tracking-wide hover:bg-yellow-100 disabled:opacity-60"
                  >
                    Move it
                  </button>
                </div>
              ) : (
                offer && (
                <div className="mt-3">
                  {pending ? (
                    <GuestSlotConfirmCard
                      slot={pending}
                      handle={offer.handle}
                      busy={busy}
                      onConfirm={() => void take(pending)}
                      onCancel={() => setPending(null)}
                    />
                  ) : (
                    <BookingCalendar
                      slots={offer.slots}
                      slotMinutes={offer.minutes}
                      onPick={(slot) => setPending(slot)}
                      allowTimezoneChange
                    />
                  )}
                </div>
                )
              )
            }
          />
        </div>
      </div>
    </div>
  );
}

interface Offer {
  token: string;
  sessionId: string;
  handle: string;
  slots: CalendarSlot[];
  minutes: number;
}

function bookedLine(result: ConfirmResult): string {
  return `Booked, ${when(result.start)}. It is in both calendars and you have an invitation.`;
}

function held(result: ConfirmResult): string {
  return `Held for you, ${when(result.start)}. They have been asked, and you will hear either way.`;
}

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
